// PostDetail.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Post from '../container/Post.js';
import PostMedia from '../container/PostMedia.js';
import PostSubscribeAndUnlockButton from '../container/PostSubscribeAndUnlockButton.js';
import Redirect from '../container/Redirect.js';

import {
  headerHeight,
  footerNavigationHeight,
  iPhoneXSeriesFooterPaddingInPWA,
  breakpoint,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import { ButtonId } from '../resource/trackingConstants.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { ProductType, SHOP_ID } from '../resource/shopConstants.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';

import CloseIconSource from '../../img/ic_topbar_close_white.svg';
import errorRoute from '../resource/errorRoute.js';

const componentZIndex = {
  back: 5, // it's render after flix gesture and flix replay button, should equal or higher than flix gesture and flix replay button
  subscribeAndUnlock: 5, // it's render after flix gesture and flix replay button, should equal or higher than flix gesture and flix replay button
};

class PostDetail extends React.PureComponent {
  constructor(props) {
    super(props);
    // fetch data for SSR
    const {
      staticContext,
      messageId,
      defaultSubscriptionCurrency,
      isSubscriptionProductFetched,
      fetchMessageDetail,
      fetchUser,
      fetchDiamondPacks,
      senderId,
      senderName,
      detailError,
    } = props;
    if (staticContext.actions) {
      if (detailError) {
        staticContext.error = detailError;
      }
      if (messageId && !senderId) {
        staticContext.actions.push({
          function: fetchMessageDetail,
          argumentObject: {
            messageId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (senderId && !senderName) {
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            id: senderId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (senderId && !isSubscriptionProductFetched) {
        staticContext.actions.push({
          function: fetchDiamondPacks,
          argumentObject: {
            currency: defaultSubscriptionCurrency,
            shopId: SHOP_ID.subscription,
            type: ProductType.SUBSCRIPTIONS,
            productIds: [senderId],
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
    }
  }
  handleClickBack = () => {
    const { goBack } = this.props;
    return goBack();
  };

  render() {
    const {
      chatroomId,
      messageId,
      deviceWidth,
      detailError,
      isChatroomMessageLayout,
      shouldShowChatroomMediaLibrary,
    } = this.props;
    const isOnMobile = deviceWidth <= breakpoint.tablet;
    if (detailError?.message) {
      const { path, status } = errorRoute(detailError?.message);

      return <Redirect to={path} status={status} replace />;
    }

    return (
      <StyledPostDetail shouldPadFooter={getIsIphoneXSeriesPWA()}>
        <Body>
          <Container isOnMobile={isOnMobile}>
            {!isOnMobile && (
              <MediaSide>
                <PostMediaWrapper>
                  <PostMedia
                    postId={messageId}
                    isOnMobile={isOnMobile}
                    isIntersecting={true}
                    isIntersected={true}
                  />
                  <SubscribeAndUnlockButtonWrapper>
                    <PostSubscribeAndUnlockButton
                      postId={messageId}
                      isIntersected
                    />
                  </SubscribeAndUnlockButtonWrapper>
                </PostMediaWrapper>
                <GoBackButton
                  data-element_id={ButtonId.All.ButtonBack}
                  onClick={this.handleClickBack}
                >
                  <img src={CloseIconSource} alt="Go back" />
                </GoBackButton>
              </MediaSide>
            )}
            <PostSide isOnMobile={isOnMobile}>
              <PostWrapper>
                <Post
                  chatroomId={chatroomId}
                  messageId={messageId}
                  shouldShowMedias={isOnMobile}
                  shouldShowSubscribeAndUnlock={isOnMobile}
                  isChatroomMessageLayout={isChatroomMessageLayout}
                  shouldShowChatroomMediaLibrary={
                    shouldShowChatroomMediaLibrary
                  }
                />
              </PostWrapper>
            </PostSide>
          </Container>
        </Body>
      </StyledPostDetail>
    );
  }
}

PostDetail.propTypes = {
  staticContext: PropTypes.object,
  chatroomId: PropTypes.string,
  messageId: PropTypes.string,
  detailError: PropTypes.instanceOf(Error),
  senderName: PropTypes.string,
  senderId: PropTypes.string,
  defaultSubscriptionCurrency: PropTypes.string,
  deviceWidth: PropTypes.number,
  isSubscriptionProductFetched: PropTypes.bool,
  isChatroomMessageLayout: PropTypes.bool,
  shouldShowChatroomMediaLibrary: PropTypes.bool,
  goBack: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchDiamondPacks: PropTypes.func,
};

PostDetail.defaultProps = {
  staticContext: {},
  chatroomId: null,
  messageId: null,
  detailError: null,
  originalTitle: '',
  captionText: '',
  senderName: '',
  senderId: '',
  defaultSubscriptionCurrency: null,
  deviceWidth: 0,
  isSubscriptionProductFetched: false,
  isChatroomMessageLayout: false,
  shouldShowChatroomMediaLibrary: false,
  goBack: () => null,
  fetchMessageDetail: () => null,
  fetchUser: () => null,
  fetchDiamondPacks: () => null,
};

const StyledPostDetail = styled.div`
  padding-top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  padding-bottom: 0;
  ${media.mobile`
    padding-bottom: ${({ shouldPadFooter }) =>
      footerNavigationHeight +
      (shouldPadFooter ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
  `};
  min-height: 100vh;
`;

const Body = styled.main``;

const PostWrapper = styled.div`
  margin-bottom: 12px;
`;

const Container = styled.div`
  ${({ isOnMobile }) =>
    isOnMobile
      ? css`
          max-width: 680px;
          margin: 0 auto;
        `
      : css`
          display: flex;
          flex-direction: row;
          height: calc(100vh - ${headerHeight}px);
          width: 100%;
          background: #191919;
        `}
`;

const PostSide = styled.div`
  padding-top: 16px;
  ${({ isOnMobile }) =>
    isOnMobile
      ? css`
          width: 100%;
        `
      : css`
          position: relative;
          display: flex;
          flex-direction: column;
          width: 360px;
          border-left: 1px solid rgba(100, 100, 100, 0.15);
          max-height: 100%;
          overflow-y: auto;
          ::-webkit-scrollbar {
            display: none;
          }
        `};
`;

const PostMediaWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const MediaSide = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const SubscribeAndUnlockButtonWrapper = styled.div`
  position: absolute;
  z-index: ${componentZIndex.subscribeAndUnlock};
  left: 0px;
  right: 0px;
  bottom: 60px;
  padding: 0px 16px;
  ${media.mobile`
    bottom: 0px;
  `};
`;

const GoBackButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 16px;
  left: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(100, 100, 100, 0.2);
  z-index: ${componentZIndex.back};
`;

export default withFetchTranslation(PostDetail);
